import { initializeApp, type FirebaseApp } from 'firebase/app'
import { getAuth, RecaptchaVerifier, type Auth } from 'firebase/auth'

// Firebase web config — mirrors the mobile EXPO_PUBLIC_FIREBASE_* vars
// because the web app uses the same Firebase project as the mobile app.
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
}

let app: FirebaseApp | null = null
let auth: Auth | null = null

export function getFirebaseAuth(): Auth {
  if (!auth) {
    if (!firebaseConfig.apiKey) {
      // Developer-facing detail goes to the console only — the thrown
      // error's `message` is rendered to end users by callers via
      // `setError(err.message)`, so it must stay generic and friendly.
      console.error(
        '[Firebase] VITE_FIREBASE_* env vars are missing — copy .env.example to .env.local and fill in the web config.',
      )
      throw new Error('Phone verification is temporarily unavailable. Please try again later.')
    }
    app = initializeApp(firebaseConfig)
    auth = getAuth(app)
  }
  return auth
}

// reCAPTCHA binds to a DOM mount point (#recaptcha-container). We do NOT
// keep one verifier for the page lifetime — see #32.
let verifier: RecaptchaVerifier | null = null

export function getRecaptchaVerifier(containerId = 'recaptcha-container'): RecaptchaVerifier {
  // #32 — hand back a FRESH verifier bound to the CURRENT element every time.
  // The invisible RecaptchaVerifier is tied to the DOM node that existed when
  // it was constructed. Across successive signups in one browser session,
  // React unmounts/remounts the auth form, so a cached verifier ends up
  // pointing at a removed node → "reCAPTCHA client element has been removed"
  // on the 2nd+ attempt. Clearing any prior instance and re-creating right
  // before signInWithPhoneNumber guarantees a live binding each time.
  resetRecaptcha()
  verifier = new RecaptchaVerifier(getFirebaseAuth(), containerId, {
    size: 'invisible',
  })
  return verifier
}

export function resetRecaptcha(): void {
  if (verifier) {
    try { verifier.clear() } catch { /* element already gone — ignore */ }
    verifier = null
  }
}
